import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, LogOut, User, ShoppingCart, ChevronDown, MessageSquare } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { base44 } from '@/api/base44Client';
import { useCart } from '@/lib/CartContext';
import { resolveDisplayName } from '@/lib/displayName';
import { useAuth } from '@/lib/AuthContext';
import { useChat } from '@/lib/ChatContext';
import { useBrandAsset } from '@/lib/BrandAssetsContext';
import AdminNavMenu from '@/components/layout/AdminNavMenu';

const LIVE_CHILDREN = [
  { label: 'Live Feeds', path: '/live' },
  { label: 'Twitch Stream', path: '/twitch' },
  { label: 'Lap Times', path: '/leaderboard' },
];

const NAV_LINKS = [
  { label: 'SHOP', children: [
    { label: 'All Products', path: '/shop' },
    { label: 'Gift Cards', path: '/gift-cards' },
  ]},
  { label: 'SIMULATORS', path: '/simulators' },
  { label: 'BOOKINGS', children: [
    { label: 'Individual Sessions', path: '/bookings' },
    { label: 'Parties & Groups', path: '/parties' },
    { label: 'Corporate Events', path: '/corporate' },
  ]},
  { label: 'LIVE', desktopExtra: true, children: LIVE_CHILDREN },
  { label: 'BLOG', path: '/blog' },
  { label: 'ABOUT', path: '/about' },
  { label: 'CONTACT', path: '/contact' },
];

export default function Navbar({ topOffset = false } = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const { totalItems } = useCart();
  const { available: chatAvailable, isOpen: chatOpen, open: openChat, close: closeChat } = useChat();
  const brandLogo = useBrandAsset('brand_logo');

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => { setIsOpen(false); }, [location]);

  return (
    <>
      <nav className={`fixed ${topOffset ? 'top-8' : 'top-0'} left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0d0d0f]/95 backdrop-blur-md shadow-lg' : 'bg-[#0d0d0f]/80 backdrop-blur-sm'
      }`}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">

          {/* Logo */}
          <Link to="/" className="shrink-0 lg:mr-6 xl:mr-10">
            <img
              src={brandLogo || "https://media.base44.com/images/public/69e7659143303e8153678f84/d1f090528_Asset1.png"}
              alt="Motion Reality Sim Racing"
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-5 xl:gap-6">
            {NAV_LINKS.filter((l) => !l.desktopExtra).map((link) =>
              link.children ? (
                <div key={link.label} className="relative group">
                  <button className="flex items-center gap-1 whitespace-nowrap text-[13px] font-medium tracking-wide text-white/70 hover:text-white transition-colors">
                    {link.label}
                    <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
                  </button>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 opacity-0 invisible group-hover:visible group-hover:opacity-100 transition-all duration-150 z-50">
                    <div className="bg-[#111115] border border-white/10 shadow-xl py-1 min-w-[170px]">
                      {link.children.map((c) => (
                        <Link
                          key={c.path}
                          to={c.path}
                          className={`block px-4 py-2 text-xs font-bold tracking-widest transition-colors ${
                            location.pathname === c.path ? 'text-primary' : 'text-white/60 hover:text-white hover:bg-white/5'
                          }`}
                        >
                          {c.label.toUpperCase()}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`group relative whitespace-nowrap text-[13px] font-medium tracking-wide transition-colors duration-200 ${
                    location.pathname === link.path ? 'text-primary' : 'text-white/70 hover:text-white'
                  }`}
                >
                  {link.label}
                  <span className={`absolute -bottom-1.5 left-0 h-px bg-primary transition-all duration-200 ${
                    location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                  }`} />
                </Link>
              )
            )}
          </div>

          <span className="hidden lg:block w-0.5 h-7 bg-white/25 mx-5" />

          {/* Right side */}
          <div className="hidden lg:flex items-center gap-6">
            {/* Contact & live zone */}
            <div className="flex items-center gap-2.5">
              <div className="flex items-center gap-1 rounded-sm bg-white/[0.03] border border-white/10 px-1 py-0.5">
                <a
                  href="https://wa.me/447411254009"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Chat with Motion Reality on WhatsApp"
                  title="WhatsApp us · 07411 254009"
                  className="flex items-center justify-center w-7 h-7 text-[#25D366] hover:text-white hover:bg-[#25D366] rounded-sm transition-colors"
                >
                  <WhatsAppIcon className="w-4 h-4" />
                </a>
                <span className="w-px h-4 bg-white/10" />
                <a
                  href="tel:01515283001"
                  title="Call us · 0151 528 3001"
                  className="flex items-center gap-1.5 whitespace-nowrap text-xs text-white/60 hover:text-white px-1.5 h-7 transition-colors"
                >
                  <Phone className="w-3.5 h-3.5" />
                  0151 528 3001
                </a>
              </div>
              <span className="w-px h-6 bg-white/10" />
              <LiveNavDropdown />
            </div>

            <span className="w-px h-6 bg-white/10" />

            {chatAvailable && (
              <button
                onClick={() => (chatOpen ? closeChat() : openChat())}
                aria-label="Open live chat"
                title="Live Chat"
                className={`flex items-center justify-center w-9 h-9 rounded-sm border transition-colors ${
                  chatOpen
                    ? 'bg-primary border-primary text-white'
                    : 'bg-white/[0.03] border-white/10 text-primary hover:bg-primary hover:text-white'
                }`}
              >
                <MessageSquare className="w-4 h-4" />
              </button>
            )}

            <Link to="/account" aria-label="Account" title={user ? resolveDisplayName(user) : 'Sign in / Account'} className={`relative transition-colors ${location.pathname === '/account' ? 'text-primary' : 'text-white/60 hover:text-white'}`}>
              <User className="w-5 h-5" aria-hidden="true" />
            </Link>
            <Link to="/cart" aria-label={`Basket${totalItems > 0 ? `, ${totalItems} item${totalItems > 1 ? 's' : ''}` : ''}`} className="relative text-white/60 hover:text-white transition-colors">
              <ShoppingCart className="w-5 h-5" aria-hidden="true" />
              {totalItems > 0 && (
                <span className="absolute -top-2 -right-2 bg-primary text-white text-[10px] font-black w-4 h-4 flex items-center justify-center rounded-full" aria-hidden="true">
                  {totalItems > 9 ? '9+' : totalItems}
                </span>
              )}
            </Link>
            {user ? (
              user.role === 'admin' ? (
                <AdminNavMenu user={user} />
              ) : (
                <button
                  onClick={() => base44.auth.logout('/')}
                  className="flex items-center gap-1.5 text-sm text-white/40 hover:text-white transition-colors"
                  title="Sign out"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              )
            ) : null}
          </div>

          {/* Mobile cart + toggle */}
          <div className="flex items-center gap-4 lg:hidden">
            <Link to="/cart" aria-label={`Basket${totalItems > 0 ? `, ${totalItems} item${totalItems > 1 ? 's' : ''}` : ''}`} className="relative text-white/60 hover:text-white transition-colors">
              <ShoppingCart className="w-5 h-5" aria-hidden="true" />
              {totalItems > 0 && (
                <span className="absolute -top-2 -right-2 bg-primary text-white text-[10px] font-black w-4 h-4 flex items-center justify-center rounded-full" aria-hidden="true">
                  {totalItems > 9 ? '9+' : totalItems}
                </span>
              )}
            </Link>
            <button
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
              className="text-white/80 hover:text-white"
            >
              {isOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-[#0d0d0f] flex flex-col items-center justify-start gap-6 pt-20 pb-8 overflow-y-auto"
          >
            {NAV_LINKS.map((link, i) => (
              <motion.div
                key={link.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.07 }}
                className="flex flex-col items-center gap-3"
              >
                {link.children ? (
                  <>
                    <span className="text-lg font-bold tracking-widest text-white/30">{link.label}</span>
                    {link.children.map((c) => (
                      <Link
                        key={c.path}
                        to={c.path}
                        className={`text-2xl font-bold tracking-wide ${
                          location.pathname === c.path ? 'text-primary' : 'text-white/70 hover:text-white'
                        }`}
                      >
                        {c.label}
                      </Link>
                    ))}
                  </>
                ) : (
                  <Link
                    to={link.path}
                    className={`text-2xl font-bold tracking-wide ${
                      location.pathname === link.path ? 'text-primary' : 'text-white/70 hover:text-white'
                    }`}
                  >
                    {link.label}
                  </Link>
                )}
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: NAV_LINKS.length * 0.07 }}
              className="flex items-center gap-6"
            >
              <Link to="/account" className="flex items-center gap-2 text-2xl font-bold tracking-wide text-white/70 hover:text-white">
                <User className="w-6 h-6" />
                ACCOUNT
              </Link>
              <Link to="/cart" className="flex items-center gap-2 text-2xl font-bold tracking-wide text-white/70 hover:text-white">
                <ShoppingCart className="w-6 h-6" />
                BASKET
                {totalItems > 0 && (
                  <span className="bg-primary text-white text-xs font-black w-6 h-6 flex items-center justify-center rounded-full">
                    {totalItems > 9 ? '9+' : totalItems}
                  </span>
                )}
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: NAV_LINKS.length * 0.07 + 0.07 }}
              className="flex items-center gap-6"
            >
              <a href="https://wa.me/447411254009" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xl font-bold tracking-wide text-white/70 hover:text-[#25D366] transition-colors">
                <WhatsAppIcon className="w-6 h-6 text-[#25D366]" />
                WHATSAPP
              </a>
              <a href="tel:01515283001" className="flex items-center gap-2 text-xl font-bold tracking-wide text-white/70 hover:text-white transition-colors">
                <Phone className="w-5 h-5" />
                0151 528 3001
              </a>
            </motion.div>
            {user?.role === 'admin' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: NAV_LINKS.length * 0.07 + 0.07 }}
              >
                <Link to="/admin/dashboard" className="text-2xl font-bold tracking-wide text-primary">
                  ADMIN DASHBOARD
                </Link>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function LiveNavDropdown() {
  const location = useLocation();
  return (
    <div className="relative group">
      <button className="flex items-center gap-1.5 whitespace-nowrap text-[13px] font-bold tracking-wider text-emerald-400 border border-emerald-400/50 animate-live-flash px-2.5 py-1 rounded-sm transition-colors hover:text-emerald-300">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        LIVE
        <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
      </button>
      <div className="absolute right-0 top-full pt-2 opacity-0 invisible group-hover:visible group-hover:opacity-100 transition-all duration-150 z-50">
        <div className="bg-[#111115] border border-emerald-400/20 shadow-xl py-1 min-w-[170px]">
          {LIVE_CHILDREN.map((c) => (
            <Link
              key={c.path}
              to={c.path}
              className={`block px-4 py-2 text-xs font-bold tracking-widest transition-colors ${
                location.pathname === c.path ? 'text-emerald-400' : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
            >
              {c.label.toUpperCase()}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function WhatsAppIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}